import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { sessionService } from '../services/session.service';
import { HUMAN_REVIEW_VERSION } from '../constants/legal';

const SHOW_ONE_TIME_PASS = false;

const SubscribePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [showHumanReviewModal, setShowHumanReviewModal] = useState(false);
  const [humanReviewChecked, setHumanReviewChecked] = useState(false);
  const [humanReviewAccepted, setHumanReviewAccepted] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'annual' | 'pay_per_ticket' | 'human_review_addon'>('annual');
  const [billingActive, setBillingActive] = useState<boolean | null>(null);
  const [humanReviewRemaining, setHumanReviewRemaining] = useState<number>(0);
  const [humanReviewEligible, setHumanReviewEligible] = useState(false);

  const returnUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('return') || '/';
    return raw.startsWith('/') ? raw : '/';
  }, [location.search]);

  const sessionIdFromReturn = useMemo(() => {
    const match = returnUrl.match(/\/ticket\/([^/]+)/);
    return match ? match[1] : null;
  }, [returnUrl]);

  React.useEffect(() => {
    if (!sessionIdFromReturn) return;
    let active = true;
    const loadSession = async () => {
      try {
        const response = await sessionService.getAppealSession(sessionIdFromReturn);
        if (!active) return;
        const accepted =
          response?.humanReviewDisclaimerAcceptedAt &&
          response?.humanReviewDisclaimerVersion === HUMAN_REVIEW_VERSION;
        setHumanReviewAccepted(!!accepted);
      } catch (error) {
        // ignore load errors
      }
    };
    loadSession();
    return () => {
      active = false;
    };
  }, [sessionIdFromReturn]);

  React.useEffect(() => {
    let active = true;
    const loadBilling = async () => {
      try {
        const response = await sessionService.getBillingMe(true);
        if (!active) return;
        setBillingActive(!!response?.active);
        setHumanReviewRemaining(response?.humanReview?.remaining ?? 0);
        setHumanReviewEligible(!!response?.active);
      } catch (error) {
        if (!active) return;
        setBillingActive(null);
        setHumanReviewRemaining(0);
        setHumanReviewEligible(false);
      }
    };
    loadBilling();
    return () => {
      active = false;
    };
  }, []);

  const startCheckout = async (planOverride?: 'annual' | 'pay_per_ticket' | 'human_review_addon') => {
    setSubmitting(true);
    try {
      const planToUse = planOverride || selectedPlan;
      const response = await sessionService.createCheckoutSession(returnUrl, planToUse);
      if (response?.url) {
        window.location.href = response.url;
        return;
      }
      toast.error('Unable to start checkout.');
    } catch (error: any) {
      toast.error(error.message || 'Unable to start checkout.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async (planOverride?: 'annual' | 'pay_per_ticket' | 'human_review_addon') => {
    if (submitting) return;
    const planToUse = planOverride || selectedPlan;
    if (planToUse !== 'pay_per_ticket' && !humanReviewAccepted) {
      setShowHumanReviewModal(true);
      return;
    }
    await startCheckout(planToUse);
  };

  const handleConfirmHumanReview = async () => {
    if (!humanReviewChecked) return;
    if (sessionIdFromReturn) {
      try {
        await sessionService.acceptHumanReviewDisclaimer(sessionIdFromReturn, {
          version: HUMAN_REVIEW_VERSION,
        });
      } catch (error) {
        toast.error('Unable to save the human review acknowledgment.');
        return;
      }
    } else {
      localStorage.setItem('humanReviewAcceptedVersion', HUMAN_REVIEW_VERSION);
    }
    // TODO: analytics event for human review disclaimer accepted
    setHumanReviewAccepted(true);
    setShowHumanReviewModal(false);
    setHumanReviewChecked(false);
    await startCheckout();
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2] text-gray-800">
      <div className="relative overflow-hidden py-14">
        <div className="pointer-events-none absolute -top-32 right-0 h-72 w-72 rounded-full bg-[#E5C564]/15 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-0 h-72 w-72 rounded-full bg-[#1B263B]/5 blur-3xl" />
        <div className="mx-auto w-full max-w-4xl px-4">
          <div className="text-center">
            <h1 className="font-display text-4xl font-bold text-[#1B263B] md:text-5xl">
              Annual Plan
            </h1>
            <p className="mt-3 text-base text-gray-500 md:text-lg">
              Prepare your Toronto parking ticket appeal with step-by-step guidance and AI-generated letters.
            </p>
          </div>

          <div className="relative mt-10">
            <div className="pointer-events-none absolute left-1/2 top-1/2 h-[110%] w-[110%] -translate-x-1/2 -translate-y-1/2 rounded-[3rem] bg-[#E5C564]/10 blur-3xl" />
            <div className="relative rounded-3xl border border-[#D4AF37]/40 bg-[#FDFCF8] p-1 shadow-[0_25px_50px_-12px_rgba(27,38,59,0.15)]">
              <div className="absolute left-1/2 top-0 z-20 -translate-x-1/2 rounded-b-xl bg-gradient-to-r from-[#D4AF37] to-[#B59226] px-6 py-1.5 text-xs font-bold uppercase tracking-widest text-white shadow-lg">
                Best Value
              </div>
              <div className="rounded-2xl bg-white px-8 pb-10 pt-10 md:px-10">
                <div className="flex flex-col items-center border-b border-gray-100 pb-8">
                  <div className="flex items-baseline gap-2 text-[#1B263B]">
                    <span className="font-display text-6xl font-semibold md:text-7xl">CA$19.99</span>
                    <span className="text-xl font-semibold text-[#B59226]">/ year</span>
                  </div>
                  <p className="mt-3 rounded-full bg-[#E5C564]/15 px-3 py-1 text-sm font-medium text-[#B59226]">
                    Best for most drivers
                  </p>
                </div>

                <div className="mt-8 rounded-xl bg-[#1B263B] p-5 text-white shadow-lg">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#E5C564]/20 text-[#E5C564]">
                      <span className="material-icons-outlined text-2xl">room_service</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-display text-lg font-semibold">
                        Human Review Included (2× / year)
                        <span className="ml-2 rounded-md bg-[#D4AF37] px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">
                          +2
                        </span>
                      </h3>
                      <p className="mt-1 text-sm text-gray-300">
                        Reviewed by experienced dispute staff for clarity and completeness. Not legal advice.
                      </p>
                    </div>
                  </div>
                </div>

                <ul className="mt-8 space-y-4 text-sm text-gray-600">
                  <li className="flex items-start gap-3">
                    <span className="material-icons-outlined text-[#D4AF37]">check_circle</span>
                    Step-by-step appeal guidance (no legal knowledge needed)
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="material-icons-outlined text-[#D4AF37]">auto_awesome</span>
                    AI-generated appeal letters
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="material-icons-outlined text-[#D4AF37]">upload_file</span>
                    Upload evidence (receipts, signage photos)
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="material-icons-outlined text-[#D4AF37]">folder_shared</span>
                    Use across multiple tickets (fair use limits apply)
                  </li>
                  <li className="flex items-start gap-3 opacity-75">
                    <span className="material-icons-outlined text-gray-400">lock</span>
                    Privacy-first storage &amp; secure checkout
                  </li>
                </ul>

                <button
                  type="button"
                  onClick={() => {
                    setSelectedPlan('annual');
                    handleConfirm('annual');
                  }}
                  disabled={submitting}
                  className="mt-10 w-full rounded-xl bg-gradient-to-b from-[#E5C564] to-[#B59226] py-5 text-lg font-semibold text-white shadow-[0_18px_40px_-20px_rgba(27,38,59,0.35)] transition-all hover:-translate-y-1 hover:shadow-[0_22px_50px_-18px_rgba(27,38,59,0.45)] disabled:opacity-60"
                >
                  <span className="flex items-center justify-center gap-2">
                    Continue with Best Value
                    <span className="material-icons-outlined text-xl">arrow_forward</span>
                  </span>
                </button>

                <p className="mt-4 text-center text-xs text-gray-400">
                  Secure checkout.
                </p>
              </div>
            </div>
          </div>

          {SHOW_ONE_TIME_PASS && (
            <div className="mt-8 text-center">
              <button
                type="button"
                onClick={() => {
                  setSelectedPlan('pay_per_ticket');
                  handleConfirm('pay_per_ticket');
                }}
                className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-[#1B263B] transition-colors"
              >
                Only disputing one ticket?{' '}
                <span className="font-semibold text-gray-700">Get a one-time pass for CA$9.99</span>
              </button>
            </div>
          )}

          {humanReviewEligible && humanReviewRemaining <= 0 && (
            <div className="mx-auto mt-10 w-full max-w-2xl rounded-2xl border border-[#D4AF37]/40 bg-white px-8 py-6 shadow-[0_16px_35px_-28px_rgba(27,38,59,0.22)]">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-[#1B263B]">Need more Human Review?</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Add 1 Human Review (non-lawyer) for clarity and completeness.
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-xl font-bold text-[#1B263B]">CA$4.99</span>
                  <div className="text-xs text-gray-400">per add-on</div>
                </div>
              </div>
              <div className="mt-4 flex flex-col gap-3 text-sm text-gray-500 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-xs text-gray-400">Available to annual subscribers only.</span>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedPlan('human_review_addon');
                    handleConfirm('human_review_addon');
                  }}
                  disabled={submitting}
                  className="rounded-xl bg-gradient-to-r from-[#D4AF37] to-[#B59226] px-4 py-2 text-sm font-semibold text-white shadow-[0_10px_30px_-12px_rgba(27,38,59,0.3)] disabled:opacity-60"
                >
                  Buy add-on
                </button>
              </div>
            </div>
          )}

          <div className="mt-12 flex gap-6 text-sm text-gray-400">
            <a className="hover:text-gray-600" href="/terms">Terms of Service</a>
            <a className="hover:text-gray-600" href="/privacy">Privacy Policy</a>
            <a className="hover:text-gray-600" href="/contact">Contact Support</a>
          </div>
          <div className="mt-2 text-xs text-gray-400">
            DisputeBot is not a law firm and does not provide legal advice.
          </div>
        </div>
      </div>

      {showHumanReviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-[#E7D9B8] bg-white p-6 shadow-[0_20px_45px_-30px_rgba(15,23,42,0.35)]">
            <div className="space-y-3">
              <h2 className="font-display text-2xl font-bold text-slate-900">About Human Review</h2>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>• Reviewed by experienced dispute staff.</li>
                <li>• Reviewers are not lawyers.</li>
                <li>• No legal advice is provided.</li>
                <li>• Review focuses on clarity and completeness.</li>
              </ul>
              <label className="mt-4 flex items-start gap-3 text-sm text-slate-700">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-[#C6A87C] focus:ring-[#C6A87C]"
                  checked={humanReviewChecked}
                  onChange={(event) => setHumanReviewChecked(event.target.checked)}
                />
                <span>
                  I understand this review is not legal advice and reviewers are not lawyers.
                </span>
              </label>
            </div>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                className="rounded-xl border border-[#E7D9B8] px-4 py-2 text-sm font-semibold text-slate-600"
                onClick={() => {
                  setShowHumanReviewModal(false);
                  setHumanReviewChecked(false);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!humanReviewChecked}
                className="rounded-xl bg-gradient-to-r from-[#C6A87C] to-[#A7854F] px-4 py-2 text-sm font-semibold text-white shadow-[0_10px_30px_-12px_rgba(166,133,79,0.6)] disabled:opacity-60"
                onClick={handleConfirmHumanReview}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscribePage;
